import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SHOW_CITATION_PILLS_FEATURE_FLAG } from '@/components/dashbaord/assistant/featureFlags.ts';
import useAssistantTurnStream from '@/hooks/assistant/useAssistantTurnStream.ts';
import { AssistantQuery, CancellableQuery } from '@/components/dashbaord/assistant/types.ts';
import { showNotification } from '@/config/notifications.ts';
import {
	createCustomQuery,
	createQueryFromExchange,
	filterResolvedOfferReplies,
	isQueryCompleted,
	isQueryError,
	isQueryIdle,
	querySelectors,
} from '@/components/dashbaord/assistant/queryUtil.ts';
import useHouseDocuments from '@/hooks/house/documents/useHouseDocuments.ts';
import useImportantQueries from '@/hooks/assistant/useImportantQueries.ts';
import { DocumentWithDownloadUrl as Document, ExchangeOut } from '@/client';
import { makeLogger } from '@/utils/logger/logger';

const logger = makeLogger('assistant-context');

interface AssistantContextType {
	// State
	queries: AssistantQuery[];
	activeQueryId: string | null;

	// Derived state
	chatMode: boolean;
	activeQuery: AssistantQuery | null;
	isQueryActive: boolean;
	isExecutingQuery: boolean;
	currentThreadId: string | null;

	// Predefined data
	/**
	 * List of documents available in the house,
	 * which will be used to answer queries, and link to documents in the response
	 */
	documents: Document[];
	documentsQuery: ReturnType<typeof useHouseDocuments>;

	/**
	 * List of important queries generated from the important information document
	 * These queries help users quickly find important information about their house
	 */
	importantItems: AssistantQuery[];
	importantQuery: ReturnType<typeof useImportantQueries>;

	/** Per-request citation style ("per_claim" | "grouped"), sent as client_context on each turn. */
	citationMode: string;
	setCitationMode: (mode: string) => void;
	/** How citations render in the chat: true = source pills, false = compact [n] markers. */
	showCitationPills: boolean;
	setShowCitationPills: (show: boolean) => void;

	// Actions
	/** Submit a query by text (no form event) — used by cross-screen handoffs so one submit both
	 * navigates here and runs the query. */
	submitQueryText: (query: string) => Promise<void>;
	cancelQuery: (queryId: string) => void;
	handleExistingQueryClick: (params: AssistantQuery) => Promise<void>;
	resetAssistant: () => Promise<void>;
	/** Resumes a past session — populates the chat with historical exchanges and routes the next message to that session */
	resumeSession: (sessionId: string, exchanges: ExchangeOut[]) => void;
	/** The session_id of the currently active conversation, or null if no session has started */
	currentSessionId: string | null;
}

interface AssistantInputContextType {
	inputQuery: string;
	setInputQuery: (query: string) => void;
	handleQuerySubmit: (e: React.FormEvent) => Promise<void>;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);
const AssistantInputContext = createContext<AssistantInputContextType | undefined>(undefined);

/**
 * Provider component for the assistant context
 *
 * Manages all state and actions for the assistant interface
 */
export const AssistantProvider: React.FC<{
	children: ReactNode;
	houseId: number;
	onQuerySubmitted?: () => void;
}> = ({ children, houseId, onQuerySubmitted }) => {
	const documentsQuery = useHouseDocuments(houseId!);
	const importantQuery = useImportantQueries(houseId);

	/**
	 * Memoized list of important queries generated from the important information document
	 * These queries help users quickly find important information about their house
	 */
	const importantItems = useMemo(() => importantQuery.data, [importantQuery.data]);

	const [queries, setQueries] = useState<AssistantQuery[]>([]);
	const [activeQueryId, setActiveQueryId] = useState<string | null>(null);

	const activeQuery = useMemo(
		() => (activeQueryId ? queries.find((q) => q.id === activeQueryId) || null : null),
		[activeQueryId, queries],
	);

	const isQueryActive = useMemo(() => activeQueryId !== null, [activeQueryId]);

	const [inputQuery, setInputQuery] = useState('');
	// Store the last submitted query text to restore it if the query fails after execution
	const [lastSubmittedQuery, setLastSubmittedQuery] = useState<string | null>(null);
	// Per-request citation style sent on each turn (gated UI control). Default mirrors the backend.
	const [citationMode, setCitationMode] = useState<string>('per_claim');
	// How citations render in the chat: source pills vs compact [n] markers. Runtime-toggleable via
	// ChatOptions; seeds from the build flag.
	const [showCitationPills, setShowCitationPills] = useState<boolean>(SHOW_CITATION_PILLS_FEATURE_FLAG);

	// Builds the per-request client_context the stream hook attaches to each turn. citation_mode is a
	// control (changes the prompt); origin_route is a signal (where the request came from).
	const getClientContext = useCallback(
		() => ({ citation_mode: citationMode, origin_route: window.location.pathname }),
		[citationMode],
	);

	const isExecutingQuery = useMemo(() => activeQuery?.status === 'executing', [activeQuery]);
	const currentThreadId = useMemo(() => activeQuery?.threadId || null, [activeQuery]);

	/** Derived from queries so it stays in sync with the chat UI — no separate hook state needed */
	const currentSessionId = useMemo(() => {
		for (let index = queries.length - 1; index >= 0; index -= 1) {
			const sessionId = queries[index].sessionId;
			if (sessionId != null) {
				return sessionId;
			}
		}
		return null;
	}, [queries]);

	/**
	 * Displays an error notification to the user
	 */
	const onError = useCallback((errorMessage: string) => {
		showNotification({
			type: 'error',
			description: errorMessage,
			closable: true,
			duration: 10_000,
		});
	}, []);

	// Helper function to update a query in the queries array
	const updateQuery = useCallback((queryId: string, updates: Partial<AssistantQuery>) => {
		logger.debug('updateQuery id:', queryId, 'keys:', Object.keys(updates));
		setQueries((prevQueries) => {
			const match = prevQueries.find((q) => q.id === queryId);
			logger.debug('updateQuery match found:', !!match, 'total queries:', prevQueries.length);
			return prevQueries.map((q) => (q.id === queryId ? { ...q, ...updates } : q));
		});
	}, []);

	// NOTE: onQueryIdUpdate is kept for future SSE migration — when the backend returns a new query/exchange ID
	// mid-stream (e.g. on the first SSE event), this callback updates the local query id and active pointer.
	// const onQueryIdUpdate = useCallback((queryId: string | null) => {
	// 	if (activeQueryId && queryId) {
	// 		updateQuery(activeQueryId, { id: queryId });
	// 		setActiveQueryId(queryId);
	// 	}
	// }, [activeQueryId, updateQuery]);

	const setQueryExecuting = useCallback(
		(isExecuting: boolean) => {
			if (activeQueryId) {
				updateQuery(activeQueryId, {
					status: isExecuting ? 'executing' : 'completed',
				});
			}
		},
		[activeQueryId, updateQuery],
	);

	const {
		submitQuery: streamSubmitQuery,
		cancelQuery,
		resetSession,
		resumeSession: resumeSessionInHook,
	} = useAssistantTurnStream({
		houseId,
		onQueryUpdate: updateQuery,
		setIsExecuting: setQueryExecuting,
		onError,
		getClientContext,
	});

	/**
	 * Cancels all executing queries except the specified active one
	 * Fire-and-forget: doesn't wait for cancellation to complete
	 */
	const cancelAllActive = useCallback(() => {
		const executingQueries: CancellableQuery[] = queries.filter(
			(query) => query.status === 'executing' && query.threadId !== null,
		) as CancellableQuery[];

		if (executingQueries.length === 0) {
			logger.info('No executing queries to cancel');
			return;
		}

		logger.info(`Cancelling ${executingQueries.length} non-active executing queries`);

		// Fire-and-forget: cancel each query without waiting
		executingQueries.forEach((query) => {
			logger.info(`Cancelling query: ${query.id}.${query.threadId}`);
			updateQuery(query.id, { status: 'cancelling' });
			cancelQuery(query.id);
		});

		logger.info(`Initiated cancellation for ${executingQueries.length} queries`);
	}, [queries, cancelQuery, updateQuery]);

	const processQuery = useCallback(
		async (newQuery: AssistantQuery) => {
			logger.info('Creating new query from existing data:', newQuery.text);
			setActiveQueryId(newQuery.id);
			setQueries((prev) => {
				logger.debug('processQuery adding query', newQuery.id, 'prev count:', prev.length);
				return [...prev, newQuery];
			});

			if (isQueryIdle(newQuery) || isQueryError(newQuery)) {
				// Update status to executing
				updateQuery(newQuery.id, { status: 'executing' });
				await streamSubmitQuery(newQuery);
			}
		},
		[streamSubmitQuery, updateQuery],
	);

	/**
	 * Submits a query by text, independent of the input field's state. The input-form handler and
	 * cross-screen handoffs (e.g. ?q=… from the records page) share this so a single submit there
	 * both navigates here and runs the query — no second Enter needed.
	 */
	const submitQueryText = useCallback(
		async (rawQuery: string) => {
			const trimmedQuery = rawQuery.trim();
			if (!trimmedQuery) {
				onError('Please enter a valid query.');
				return;
			}

			// Store the query text for potential restoration if the query fails after execution.
			setLastSubmittedQuery(trimmedQuery);
			onQuerySubmitted?.();

			// Clear the input field immediately after submission.
			setInputQuery('');

			try {
				cancelAllActive();
				const userQuery = createCustomQuery(trimmedQuery);
				await processQuery(userQuery);
				// Input remains cleared on success (already done above)
			} catch (error) {
				// If the query fails immediately, restore the original input text
				setInputQuery(trimmedQuery);
				setLastSubmittedQuery(null); // Clear the stored query since we've restored it
				onError(error instanceof Error ? error.message : 'Failed to process your query. Please try again.');
			}
		},
		[cancelAllActive, processQuery, onError, setLastSubmittedQuery, onQuerySubmitted],
	);

	/** Handles form submission for the query input */
	const handleQuerySubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			await submitQueryText(inputQuery);
		},
		[inputQuery, submitQueryText],
	);

	/**
	 * Handles clicking on an existing query from the query tabs
	 *
	 * Cancels any ongoing query, loads the selected query, and submits it if needed
	 */
	const handleExistingQueryClick = useCallback(
		async (query: AssistantQuery) => {
			cancelAllActive();

			const newQuery = query;
			const cacheQuery = querySelectors.findById(queries, newQuery.id);

			if (cacheQuery) {
				logger.info('Found existing query from list:', cacheQuery.id);

				if (isQueryCompleted(cacheQuery)) {
					logger.info('Query already completed, setting as active:', cacheQuery.id);
					setActiveQueryId(cacheQuery.id);
					return;
				} else {
					logger.info('Query not completed, updating status to executing:', cacheQuery.id);
				}
			}

			await processQuery(newQuery);
		},
		[cancelAllActive, queries, processQuery, setActiveQueryId],
	);

	/**
	 * Resets the assistant to its initial state
	 *
	 * Cancels any ongoing query and clears all state variables
	 */
	const resetAssistant = useCallback(async () => {
		cancelAllActive();
		resetSession();
		setInputQuery('');
		setLastSubmittedQuery(null);
		setActiveQueryId(null);
		setQueries([]);
	}, [cancelAllActive, resetSession, setLastSubmittedQuery]);

	/**
	 * Resumes a past session from chat history.
	 * Clears the current chat UI so the user starts fresh in the resumed session context —
	 * the next message they send will be routed to the existing session on the backend.
	 */
	const resumeSession = useCallback(
		(sessionId: string, exchanges: ExchangeOut[]) => {
			cancelAllActive();
			// Convert historical exchanges into AssistantQuery objects so the chat UI shows the full
			// conversation, then drop the mechanical "yes" replies whose outcome is already folded
			// onto the offer they answered (see filterResolvedOfferReplies).
			const historicalQueries = filterResolvedOfferReplies(exchanges.map(createQueryFromExchange));
			setQueries(historicalQueries);
			setActiveQueryId(historicalQueries[historicalQueries.length - 1]?.id ?? null);
			resumeSessionInHook(sessionId);
		},
		[cancelAllActive, resumeSessionInHook],
	);

	/**
	 * Resets the assistant state when the house changes
	 */
	useEffect(() => {
		// Reset when house changes
		logger.warn('Initializing for house:', houseId);
		resetAssistant();
	}, [houseId]);

	/**
	 * Watch for changes to the active query's status and restore the original query text
	 * when the status changes to "error" after execution has started
	 */
	useEffect(() => {
		// Only proceed if we have an active query and a stored query text
		if (activeQuery && lastSubmittedQuery) {
			// If the query has failed after execution started
			if (activeQuery.status === 'error') {
				logger.info('Query failed after execution, restoring original query text');
				// Restore the original query text
				setInputQuery(lastSubmittedQuery);
				// Clear the stored query since we've restored it
				setLastSubmittedQuery(null);
				// Show an error notification
				onError('The assistant encountered an error while processing your query. Please try again.');
			} else if (activeQuery.status === 'completed' || activeQuery.status === 'cancelled') {
				// Clear the stored query when the query completes or is cancelled
				setLastSubmittedQuery(null);
			}
		}
	}, [activeQuery, lastSubmittedQuery, setInputQuery, onError]);

	const value = useMemo<AssistantContextType>(
		() => ({
			// State
			queries,
			activeQueryId,
			activeQuery,

			// Derived state
			chatMode: queries.length > 0,
			isQueryActive,
			isExecutingQuery,
			currentThreadId,

			// Predefined queries
			documents: documentsQuery.documents,
			documentsQuery,
			importantItems,
			importantQuery,

			// Actions
			submitQueryText,
			citationMode,
			setCitationMode,
			showCitationPills,
			setShowCitationPills,
			cancelQuery,
			handleExistingQueryClick,
			resetAssistant,
			resumeSession,
			currentSessionId,
		}),
		[
			activeQuery,
			activeQueryId,
			cancelQuery,
			citationMode,
			currentSessionId,
			currentThreadId,
			documentsQuery,
			handleExistingQueryClick,
			importantItems,
			importantQuery,
			isExecutingQuery,
			isQueryActive,
			queries,
			resetAssistant,
			resumeSession,
			showCitationPills,
			submitQueryText,
		],
	);

	const inputValue = useMemo<AssistantInputContextType>(
		() => ({
			inputQuery,
			setInputQuery,
			handleQuerySubmit,
		}),
		[inputQuery, handleQuerySubmit],
	);

	return (
		<AssistantContext.Provider value={value}>
			<AssistantInputContext.Provider value={inputValue}>{children}</AssistantInputContext.Provider>
		</AssistantContext.Provider>
	);
};

/**
 * Hook to access the assistant context
 *
 * Provides access to all assistant state and actions
 */
export const useAssistant = () => {
	const context = useContext(AssistantContext);
	if (context === undefined) {
		throw new Error('useAssistant must be used within an AssistantProvider');
	}
	return context;
};

export const useAssistantInput = () => {
	const context = useContext(AssistantInputContext);
	if (context === undefined) {
		throw new Error('useAssistantInput must be used within AssistantProvider');
	}
	return context;
};

/**
 * Like {@link useAssistant} but returns `undefined` instead of throwing when there is no
 * AssistantProvider above. For components that live inside the *shared* composer (AssistantInput) and
 * so render both inside the assistant (provider present) and on other screens like records/maintenance
 * (no provider) — they can bail out gracefully instead of crashing the page.
 */
export const useAssistantOptional = () => useContext(AssistantContext);
